import React, { useEffect, useRef, useState } from 'react'
import styles from '../../TemporaryPhoto.module.sass'
import { useDispatch, useSelector } from 'react-redux'
import { EmphemeralPostType } from '../../../../store/ephemeral/ephemeralTypings'
import Dropzone from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'
import { uploadPhoto } from '../../../../api/ephemeral'
import { RootState } from '../../../../store/rootReducer'
import { openMenu } from '../../../../store/common/commonSlice'
import { getLangFromPath } from '../../../../i18next'
import { useLocation } from 'react-router-dom'

interface CommentFormProps {
    isInitMessage?: boolean
    isReply?: boolean
    skeleton?: boolean
    post: EmphemeralPostType
    setAdditionalComments?: React.Dispatch<React.SetStateAction<EmphemeralPostType[]>>
    closeReply?: () => void
}

const CommentForm = ({
    isInitMessage = false,
    isReply = false,
    skeleton = false,
    post,
    setAdditionalComments,
    closeReply
}: CommentFormProps) => {
    const dispatch = useDispatch()
    const [t] = useTranslation()

    const location = useLocation()

    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)

    const [image_data, setImageData] = React.useState(undefined)
    const [commentText, setCommentText] = React.useState('')
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    const textareaRef = useRef(null)
    const [textareaWidth, setTextAreaWidth] = React.useState(0)

    const [errorText, setErrorText] = React.useState('')

    const handleSubmitComment = async () => {
        if (!loggedIn) {
            dispatch(openMenu())
            return
        }
        if (isSubmitting) return

        setIsSubmitting(true)
        setErrorText('')

        const objsToSend = image_data
            ? { parent_id: post.id, content: commentText, image_data }
            : { parent_id: post.id, content: commentText }

        try {
            const uploadPhotoResponse = await uploadPhoto(objsToSend)
            if (!uploadPhotoResponse) return

            setCommentText('')
            setImageData(undefined)
            if (closeReply) closeReply()
            if (setAdditionalComments) {
                // @ts-ignore
                setAdditionalComments((prev) => [...prev, uploadPhotoResponse.contribution])
            }
        } catch (err) {
            if (err.messages) setErrorText(err.messages[0])
            if (err.message) setErrorText(err.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault()
            handleSubmitComment()
        }

        if ((e.key === 'Enter' && e.ctrlKey) || (e.key === 'Enter' && e.shiftKey)) {
            e.preventDefault()
            setCommentText((prev) => prev + '\n')
        }
    }

    useEffect(() => {
        setTextAreaWidth(textareaRef.current.offsetWidth - 10)
    }, [post])

    useEffect(() => {
        if (errorText) setErrorText('')
    }, [commentText])

    return (
        <>
            {image_data && (
                <div className={styles['preview']}>
                    <img src={image_data} alt="" />
                    <div
                        className={styles['deleteButton']}
                        onClick={() => {
                            setImageData(undefined)
                        }}
                    ></div>
                </div>
            )}
            {errorText && <p style={{ color: '#a23330', fontSize: '12px', margin: '2px 12px' }}>{errorText}</p>}
            <form
                className={`${styles['commentForm']} ${styles['commentForm_popup']}  ${
                    isReply ? styles['commentForm_reply'] : ''
                }`}
            >
                <textarea
                    ref={textareaRef}
                    className={`${isInitMessage ? styles['initOfTopic'] : ''}${skeleton ? ' skeleton' : ''}`}
                    placeholder={skeleton ? '' : t('Leave a comment')}
                    rows={1}
                    autoFocus={isReply}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    disabled={!loggedIn || isSubmitting}
                    onKeyDown={handleKeyDown}
                />
                {!loggedIn && !skeleton && (
                    <div
                        style={{
                            position: 'absolute',
                            width: `${textareaWidth ? textareaWidth + 'px' : 'calc(100% - 80px)'}`,
                            height: 'calc(100% - 10px)',
                            marginTop: '5px',
                            marginLeft: '5px',
                            paddingLeft: '5px',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span>
                            <Trans i18nKey={'signInPhotoText'}>
                                <span
                                    style={{ color: 'var(--main-theme-color)', fontWeight: 'bold', cursor: 'pointer' }}
                                    onClick={() => dispatch(openMenu())}
                                >
                                    Sign in
                                </span>
                                or
                                <span
                                    style={{ color: 'var(--main-theme-color)', fontWeight: 'bold', cursor: 'pointer' }}
                                    onClick={() => dispatch(openMenu())}
                                >
                                    register
                                </span>
                                to use this feature
                            </Trans>
                        </span>
                    </div>
                )}
                {commentText !== '' && (
                    <img
                        src={require('./../../../../assets/ic-plus.svg')}
                        onClick={() => setCommentText('')}
                        className={`${styles['removeText']} ${styles[getLangFromPath(location.pathname)]}`}
                    />
                )}

                <div>
                    <Dropzone
                        accept="image/*"
                        multiple={false}
                        onDrop={(acceptedFiles) => {
                            if (isSubmitting) return
                            try {
                                const reader = new FileReader()
                                reader.onload = (e) => {
                                    setImageData(e.target.result)
                                }
                                reader.readAsDataURL(acceptedFiles[0])
                            } catch (err) {
                                //
                            }
                        }}
                        disabled={isSubmitting}
                    >
                        {({ getRootProps, getInputProps }) =>
                            loggedIn ? (
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <button
                                        type="button"
                                        className={skeleton ? 'skeleton' : ''}
                                        disabled={isSubmitting}
                                    >
                                        <img src={require('./../../../../assets/ic-add_attachment.svg')} />
                                    </button>
                                </div>
                            ) : (
                                <></>
                            )
                        }
                    </Dropzone>
                </div>
                <button
                    type="button"
                    onClick={handleSubmitComment}
                    className={skeleton ? 'skeleton' : ''}
                    style={{ opacity: isInitMessage ? '0.6' : 1 }}
                    disabled={isSubmitting}
                >
                    {t('Post')}
                </button>
            </form>
            <div className={`${styles['marginBlock']} ${isReply ? styles['marginBlock_reply'] : ''}`} />
        </>
    )
}

export default CommentForm
